import { flexRender } from '@tanstack/react-table';
import useTransactionalFootnoteDrawer from 'legacy-modules/report/hooks/use-transactional-footnote-drawer';
import useTranslation from 'next-translate/useTranslation';

import { CellGenerator } from './cell-generator';

export type TableBodyDataType = {
  rowId: any;
  cellId: any;
  colSpan: number;
  rowSpan: number;
  tdClassName: string | undefined;
  color: string;
  alignment: React.TdHTMLAttributes<'td'>['align'];
  linkText: any;
  content: React.ReactNode;
  sticky: number;
};

export default function useGeneratePivotTableBody() {
  const { t } = useTranslation();
  const { transactionalButton } = useTransactionalFootnoteDrawer();

  const generateTableData = (rows, keyMap, linkMap, result, props) => {
    let sticky = 0;
    return rows.map((row, rowIdx) => {
      let tdClassName: string | undefined = undefined;
      const cells = row.getVisibleCells().map((cell, cellIdx) => {
        const cellColumn = cell.column.columnDef;
        let colSpan = 1;
        let rowSpan = 1;

        // Handle aggregate row and colSpan
        if (Number.isInteger(row.original.position)) {
          tdClassName = '--aggregate';
          if (row.original.position === cellIdx) {
            colSpan =
              (result!.pivot?.value?.rows || []).length - row.original.position;
          } else if (
            cellIdx > row.original.position &&
            cellIdx < (result!.pivot?.value?.rows || []).length
          ) {
            return null;
          }
        }

        // Row and column span logic
        if (cellIdx < keyMap[rowIdx].length) {
          if (
            rowIdx > 0 &&
            keyMap[rowIdx][cellIdx] === keyMap[rowIdx - 1][cellIdx]
          ) {
            return null;
          }

          for (
            let futureRowIndex = rowIdx + 1;
            futureRowIndex < rows.length;
            futureRowIndex++
          ) {
            if (keyMap[rowIdx][cellIdx] === keyMap[futureRowIndex][cellIdx]) {
              rowSpan++;
            } else {
              break;
            }
          }
        }

        // Handle link text
        let linkText;
        const link = cellColumn.link;
        if (link) {
          const originalData = row.original[link];
          if (originalData) {
            linkText = {
              id: originalData.id,
              acl: originalData.acl.split(/(employee.|.list|.page)/)[2],
              cell: originalData.name ?? cell.getContext().getValue(),
            } as {
              id: string;
              acl: string;
              cell: React.ReactNode;
            };
          }
        }

        if (linkMap[rowIdx]?.[cellIdx]) {
          const linkData = linkMap[rowIdx][cellIdx];
          linkText = {
            id: linkData.id,
            acl: linkData.acl.split(/(employee.|.list|.page)/)[2],
            cell: linkData.name ?? cell.getContext().getValue(),
          };
        }

        // Determine cell color (0 => black, 1 => green, -1 => red)
        const colorNumber = (() => {
          const isFound = props.numberColoredFields?.some(
            (val) => val === cellColumn.accessorKey,
          );
          if (isFound) {
            const result = parseFloat(
              cell.row.original[cellColumn.accessorKey],
            );
            if (isNaN(result)) return 0;
            return result >= 0 ? 1 : -1;
          }
          return 0;
        })();

        const cellColor =
          tdClassName === '--aggregate'
            ? 'black'
            : colorNumber === 1
            ? 'green'
            : colorNumber === -1
            ? 'red'
            : 'black';

        const cellAlignment =
          cellIdx >= (result!.pivot?.value?.rows || []).length
            ? 'right'
            : 'left';

        const item = {
          rowId: row.id,
          cellId: cell.id,
          colSpan,
          rowSpan,
          tdClassName,
          color: cellColor,
          alignment: cellAlignment,
          linkText: linkText || null,
          content:
            transactionalButton(row.original, cellColumn.accessorKey) ??
            CellGenerator(cell.getValue(), t) ??
            flexRender(cellColumn.cell, cell.getContext()),
          sticky,
        } as TableBodyDataType;

        return item;
      }) as (TableBodyDataType | null)[];

      if (tdClassName === '--aggregate') {
        sticky += 1;
      }

      const _row = { cells, sticky, rowId: row.id, tdClassName };

      return _row;
    }) as {
      cells: (TableBodyDataType | null)[];
      sticky: number;
      rowId: string;
      tdClassName: string | undefined;
    }[];
  };
  return generateTableData;
}
